<template>
  <div class="guest-cards-landing">
    <!-- Hero Banner -->
    <banner
      :backgroundImage="$maxxpm.config.home.banner.image"
      class="hero-banner"
    >
      <div class="hero-content">
        <img
          :src="$maxxpm.config.logo.src"
          :alt="$maxxpm.config.logo.alt"
          class="hero-logo"
        />
        <h1 class="hero-title">Get More Information</h1>
        <h2 class="hero-subtitle">
          Learn More About Abington Heights
        </h2>
        <p class="hero-description">
          Have questions? We're here to help. Fill out the form below and we'll get back to you with all the details you need.
        </p>
        <Button
          label="Contact Us Now"
          class="hero-cta-button"
          @click="scrollToForm"
          rounded
          size="large"
        />
      </div>
    </banner>

    <!-- Form Section -->
    <div id="contact-form" class="form-section">
      <div class="container">
        <h2 class="text-primary text-center mb-4">Get In Touch</h2>
        <p class="text-center form-description">
          Fill out the form below and our team will reach out to you with personalized information about our available units, amenities, and community.
        </p>
        <div class="form-container">
          <form @submit.prevent="submitForm" class="contact-form">
            <div class="form-grid">
              <div class="form-field">
                <label for="firstName" class="form-label">
                  First Name <span class="required">*</span>
                </label>
                <InputText
                  id="firstName"
                  v-model="formData.firstName"
                  :class="{ 'p-invalid': errors.firstName }"
                  placeholder="Enter your first name"
                  class="form-input"
                />
                <small v-if="errors.firstName" class="error-message">
                  {{ errors.firstName }}
                </small>
              </div>

              <div class="form-field">
                <label for="lastName" class="form-label">
                  Last Name <span class="required">*</span>
                </label>
                <InputText
                  id="lastName"
                  v-model="formData.lastName"
                  :class="{ 'p-invalid': errors.lastName }"
                  placeholder="Enter your last name"
                  class="form-input"
                />
                <small v-if="errors.lastName" class="error-message">
                  {{ errors.lastName }}
                </small>
              </div>

              <div class="form-field">
                <label for="email" class="form-label">
                  Email <span class="required">*</span>
                </label>
                <InputText
                  id="email"
                  v-model="formData.email"
                  :class="{ 'p-invalid': errors.email }"
                  placeholder="Enter your email address"
                  type="email"
                  class="form-input"
                />
                <small v-if="errors.email" class="error-message">
                  {{ errors.email }}
                </small>
              </div>

              <div class="form-field">
                <label for="phone" class="form-label">
                  Phone Number <span class="required">*</span>
                </label>
                <InputText
                  id="phone"
                  v-model="formData.phone"
                  :class="{ 'p-invalid': errors.phone }"
                  placeholder="Enter your phone number"
                  type="tel"
                  class="form-input"
                />
                <small v-if="errors.phone" class="error-message">
                  {{ errors.phone }}
                </small>
              </div>

              <div class="form-field full-width">
                <label for="moveInDate" class="form-label">
                  Desired Move-In Date <span class="optional">(Optional)</span>
                </label>
                <Calendar
                  id="moveInDate"
                  v-model="formData.moveInDate"
                  placeholder="Select move-in date"
                  :showIcon="true"
                  dateFormat="mm/dd/yy"
                  class="form-input"
                  :minDate="new Date()"
                />
              </div>
            </div>

            <div class="form-actions">
              <Button
                type="submit"
                label="Submit"
                severity="primary"
                class="submit-button"
                rounded
                :loading="isSubmitting"
              />
            </div>

            <div v-if="submitSuccess" class="success-message">
              <i class="fas fa-check-circle"></i>
              Thank you! We'll get back to you soon.
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Key Benefits Section -->
    <div class="container mt-5 mb-5">
      <div class="grid">
        <div
          v-for="(benefit, index) in keyBenefits"
          :key="index"
          class="col-12 md:col-4"
        >
          <div class="info-block">
            <div class="benefit-icon-small">
              <i :class="benefit.icon"></i>
            </div>
            <h4 class="text-primary text-center">{{ benefit.title }}</h4>
            <p class="text-center">{{ benefit.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Photos Section -->
    <div class="grid grid-nogutter mt-5">
      <div
        v-for="(image, index) in showcaseImages"
        :key="index"
        class="p-0 col-12 md:col-4"
      >
        <img :src="image.src" :alt="image.alt" class="showcase-image" />
      </div>
    </div>

    <!-- Why Choose Us Section -->
    <div class="container mt-5 mb-5">
      <div class="grid">
        <div class="col-12 md:col-6">
          <div class="info-block">
            <h3 class="text-primary">About Our Community</h3>
            <ul class="info-list">
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Beautiful, modern townhomes in Roy, Utah</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Spacious floor plans with modern amenities</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Pet-friendly community with outdoor spaces</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Convenient location near shopping and dining</p>
              </li>
            </ul>
          </div>
        </div>
        <div class="col-12 md:col-6">
          <div class="info-block">
            <h3 class="text-primary">What We Offer</h3>
            <ul class="info-list">
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">2-car garages included with each unit</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Smart home features and technology</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Responsive property management team</p>
              </li>
              <li class="flex align-items-start">
                <img
                  :src="$maxxpm.config.listMarkerSrc"
                  class="mr-2 mt-2 li-marker"
                  role="none"
                  alt=""
                />
                <p class="mt-1">Quick and easy application process</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Call to Action -->
    <banner
      :backgroundImage="$maxxpm.config.home.midContainerImage"
      class="cta-banner"
    >
      <h3 class="text-white mt-0 text-center">Ready to Learn More?</h3>
      <p class="text-white text-center cta-subtitle">
        Get all your questions answered. Contact us today!
      </p>
    </banner>

    <!-- Contact Info -->
    <div class="container mt-5 mb-5">
      <div class="contact-info">
        <h3 class="text-primary text-center">Prefer to Reach Out Directly?</h3>
        <p class="text-center">
          Call us at
          <a href="tel:+13852870208" class="text-primary" @click="event('contact_us_phone')">{{
            $maxxpm.config.footer.phone
          }}</a>
          or <a href="mailto:info@maxxpm.com" class="text-primary" @click="event('contact_us_email')">email us</a>.
        </p>
        <p class="text-center">
          {{ $maxxpm.config.footer.address }},
          {{ $maxxpm.config.footer.cityStateZip }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { inject, ref, reactive, onMounted } from "vue";
import { Banner } from "@maxxpm/vue-components";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Calendar from "primevue/calendar";
import axios from "axios";
import { event } from "vue-gtag";

const $maxxpm = inject("$maxxpm");

const scrollToForm = () => {
  const element = document.getElementById("contact-form");
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

let propertyId = undefined
let searchParams = undefined

onMounted(async () => {
  const listings = await axios.get(`https://09udmvr09d.execute-api.us-west-2.amazonaws.com/listings?propertyGroup=${$maxxpm.config.availability.propertyGroup}&limit=10&offset=0`);
  if (listings.data.data.length > 0) {
    propertyId = listings.data.data[0].property_id;
  }
  searchParams = new URLSearchParams(window.location.search);
});

// Form data
const formData = reactive({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  moveInDate: null,
});

const errors = reactive({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
});

const isSubmitting = ref(false);
const submitSuccess = ref(false);

// Form validation
const validateForm = () => {
  let isValid = true;
  errors.firstName = "";
  errors.lastName = "";
  errors.email = "";
  errors.phone = "";

  if (!formData.firstName || formData.firstName.trim() === "") {
    errors.firstName = "First name is required";
    isValid = false;
  }

  if (!formData.lastName || formData.lastName.trim() === "") {
    errors.lastName = "Last name is required";
    isValid = false;
  }

  if (!formData.email || formData.email.trim() === "") {
    errors.email = "Email is required";
    isValid = false;
  } else {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address";
      isValid = false;
    }
  }

  if (!formData.phone || formData.phone.trim() === "") {
    errors.phone = "Phone number is required";
    isValid = false;
  } else {
    // Basic phone validation (allows common formats)
    const phoneRegex = /^[\d\s\-()+]+$/;
    if (!phoneRegex.test(formData.phone) || formData.phone.replace(/\D/g, "").length < 10) {
      errors.phone = "Please enter a valid phone number";
      isValid = false;
    }
  }

  return isValid;
};

// Form submission
const submitForm = async () => {
  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;
  submitSuccess.value = false;

  try {
    if (!propertyId) {
      throw new Error("Property ID not found");
    }
    // Format data for AppFolio guest card API
    const guestCardData = {
      FirstName: formData.firstName.trim(),
      LastName: formData.lastName.trim(),
      Email: formData.email.trim(),
      Phone: formData.phone.trim(),
      DesiredMoveInDate: formData.moveInDate
        ? formData.moveInDate.toISOString().split("T")[0]
        : null,
      PropertyId: propertyId,
      Source: searchParams ? searchParams.get("source") : "abingtonheights.com/get-more-info",
    };

    // Call AppFolio guest card API endpoint
    const apiEndpoint =
      "https://09udmvr09d.execute-api.us-west-2.amazonaws.com/guest-cards";

    await axios.post(apiEndpoint, guestCardData, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Reset form on success
    formData.firstName = "";
    formData.lastName = "";
    formData.email = "";
    formData.phone = "";
    formData.moveInDate = null;
    submitSuccess.value = true;
    event("guest_card_created");
    // Hide success message after 5 seconds
    setTimeout(() => {
      submitSuccess.value = false;
    }, 5000);
  } catch (error) {
    console.error("Error submitting form:", error);
    let errorMessage = "There was an error submitting your form. Please try again.";
    
    if (error.response) {
      // Server responded with error status
      errorMessage = error.response.data?.message || errorMessage;
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = "Unable to connect to the server. Please check your internet connection and try again.";
    }
    
    alert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const keyBenefits = [
  {
    icon: "fas fa-info-circle",
    title: "Expert Guidance",
    description:
      "Our knowledgeable team is here to answer all your questions and help you find the perfect home.",
  },
  {
    icon: "fas fa-headset",
    title: "Responsive Support",
    description:
      "We respond quickly to inquiries and are committed to providing exceptional customer service.",
  },
  {
    icon: "fas fa-clipboard-list",
    title: "Detailed Information",
    description:
      "Get comprehensive details about our units, amenities, pricing, and availability.",
  },
];

const showcaseImages = [
  {
    src: "https://cdn.maxxpm.com/abingtonheights/Abington%20Website%20Photos/23-web-or-mls-3712%20S%201940%20W-20.jpg",
    alt: "Abington Heights living space",
  },
  {
    src: "https://cdn.maxxpm.com/abingtonheights/Abington%20Website%20Photos/37-web-or-mls-3712%20S%201940%20W-34.jpg",
    alt: "Abington Heights kitchen",
  },
  {
    src: "https://cdn.maxxpm.com/abingtonheights/Abington%20Website%20Photos/34-web-or-mls-3712%20S%201940%20W-31.jpg",
    alt: "Abington Heights interior",
  },
];
</script>

<style lang="scss" scoped>
.guest-cards-landing {
  width: 100%;
  min-height: 100vh;
}

.container {
  width: 95vw;
  max-width: 1200px;
  margin: auto;
}

// Hero Banner
:deep(.hero-banner) {
  position: relative;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  
  // Add dark overlay for better text readability
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 800px;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.hero-logo {
  max-width: 300px;
  max-height: 150px;
  width: auto;
  height: auto;
  margin-bottom: 1.5rem;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.hero-subtitle {
  font-size: 2rem;
  font-weight: 400;
  color: var(--appTextColor);
  margin-bottom: 1rem;
}

.hero-description {
  font-size: 1.25rem;
  color: var(--appTextColor);
  margin-bottom: 2rem;
}

.hero-cta-button {
  :deep(.p-button) {
    padding: 1rem 2.5rem;
    font-size: 1.1rem;
    font-weight: 600;
    background: #FAAB4C;
    border: none;
    color: white;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;

    &:hover {
      background: #E89A3D;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    }

    &:active {
      transform: translateY(0);
      background: #D88A2E;
    }
  }
}

// Form Section
.form-section {
  background: #f8f9fa;
  padding: 4rem 1rem;
  margin-top: 0;
  
  .form-description {
    font-size: 1.2rem;
    margin-bottom: 2rem;
    color: var(--appTextColor);
  }
  
  .form-container {
    display: flex;
    justify-content: center;
    width: 100%;
    background: white;
    border-radius: 8px;
    padding: 2rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
}

.contact-form {
  width: 100%;
  max-width: 600px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  .form-field {
    display: flex;
    flex-direction: column;

    &.full-width {
      grid-column: 1 / -1;
    }
  }
}

.form-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--appTextColor);
  font-size: 1rem;

  .required {
    color: #dc3545;
    margin-left: 0.25rem;
  }

  .optional {
    color: #6c757d;
    font-weight: 400;
    font-size: 0.9rem;
  }
}

.form-input {
  width: 100%;

  :deep(.p-inputtext) {
    width: 100%;
    padding: 0.75rem;
    font-size: 1rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    transition: border-color 0.3s ease;

    &:focus {
      border-color: var(--primary-color);
      outline: none;
      box-shadow: 0 0 0 0.2rem rgba(250, 171, 76, 0.25);
    }

    &.p-invalid {
      border-color: #dc3545;
    }
  }

  :deep(.p-calendar) {
    width: 100%;

    .p-inputtext {
      width: 100%;
      padding: 0.75rem;
      font-size: 1rem;
    }
  }
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: block;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

.submit-button {
  :deep(.p-button) {
    padding: 0.875rem 2.5rem;
    font-size: 1.1rem;
    font-weight: 600;
    background: var(--primary-color);
    border: none;
    color: white;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;

    &:hover {
      background: #E89A3D;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    }

    &:active {
      transform: translateY(0);
      background: #D88A2E;
    }
  }
}

.success-message {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  color: #155724;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 500;

  i {
    font-size: 1.25rem;
  }
}

// Key Benefits
.benefit-icon-small {
  width: 60px;
  height: 60px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;

  i {
    font-size: 1.75rem;
    color: white;
  }
}

// Showcase Images
.showcase-image {
  width: 100%;
  height: 380px;
  object-fit: cover !important;
}

// Info Blocks
.info-block {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  height: 100%;

  h3,
  h4 {
    font-size: 1.75rem;
    margin-bottom: 1.5rem;
    text-align: center;
  }

  h4 {
    font-size: 1.5rem;
  }
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 1rem;
  }

  p {
    margin: 0;
    line-height: 1.6;
  }
}

.li-marker {
  max-width: 35px;
  flex-shrink: 0;
}

// CTA Banner
:deep(.cta-banner) {
  height: 400px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;

  h3 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }

  .cta-subtitle {
    font-size: 1.5rem;
    margin: 0;
  }
}

// Contact Info
.contact-info {
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }

  a {
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  :deep(.hero-banner) {
    min-height: 400px;
    padding: 2rem 1rem;
  }

  .hero-content {
    padding: 1.5rem;
    max-width: 100%;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.5rem;
  }

  .hero-description {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }

  .hero-logo {
    max-width: 200px;
    max-height: 100px;
    margin-bottom: 1rem;
  }

  .hero-cta-button {
    :deep(.p-button) {
      padding: 0.875rem 2rem;
      font-size: 1rem;
      width: 100%;
    }
  }

  .form-section {
    padding: 2rem 1rem;

    .form-container {
      padding: 1rem;
    }
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  :deep(.cta-banner) {
    height: 300px;

    h3 {
      font-size: 1.75rem;
    }

    .cta-subtitle {
      font-size: 1.2rem;
    }
  }

  .info-block {
    margin-bottom: 2rem;
  }
}
</style>

